import { useEffect, useMemo } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Fade,
  Typography,
} from "@mui/material";
import { useInfiniteQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { WebBackButton } from "../../../components/shared";
import { RewardsList } from "../../../components/pages/referral/rewards-list";
import { getReferralAccruals } from "../../../services/referrals";
import { useTelegram } from "../../../hooks";

const PAGE_SIZE = 30;

/**
 * Полная страница «История наград» — открывается по стрелке «Все →»
 * из блока `RewardsList` на `/referral`. Пагинация постранично: подгружаем
 * следующие 30 записей по кнопке «Загрузить ещё».
 */
export function ReferralRewardsPage() {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { t } = useTranslation("referral");

  useEffect(() => {
    tg.BackButton.show();
    const handler = () => navigate("/referral");
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  const query = useInfiniteQuery(
    ["referral", "accruals", "all"],
    ({ pageParam = 1 }) => getReferralAccruals(pageParam, PAGE_SIZE),
    {
      getNextPageParam: (last, all) => {
        const loaded = all.reduce((sum, p) => sum + p.items.length, 0);
        return loaded < last.total ? all.length + 1 : undefined;
      },
      staleTime: 30_000,
    },
  );

  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  const total = query.data?.pages[0]?.total ?? 0;
  const hasMore = query.hasNextPage;
  const isInitialLoading = query.isLoading && items.length === 0;

  return (
    <Fade in>
      <Box
        sx={{
          marginBottom: "100px",
          width: "100%",
          maxWidth: "460px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <WebBackButton to="/referral" title={t("rewards.page_title")} />

        {isInitialLoading ? (
          <Box
            sx={{
              minHeight: "40vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {total > 0 && (
              <Typography
                sx={{
                  fontSize: "12px",
                  opacity: 0.55,
                  paddingLeft: "4px",
                  marginBottom: "6px",
                }}
              >
                {t("rewards.total_count", { count: total })}
              </Typography>
            )}

            <RewardsList
              items={items}
              isLoading={false}
              limit={Number.POSITIVE_INFINITY}
              showSeeAll={false}
            />

            {hasMore && (
              <Box
                sx={{
                  marginTop: "12px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ButtonBase
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                  sx={(theme) => ({
                    padding: "10px 22px",
                    borderRadius: "40px",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "Inter",
                    color: theme.palette.mode === "dark" ? "#fff" : "#000",
                    bgcolor:
                      theme.palette.mode === "dark" ? "#26262A" : "#e0e0e0",
                    transition: "opacity 0.15s",
                    "&:active": { opacity: 0.85 },
                    "&.Mui-disabled": { opacity: 0.5 },
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  })}
                >
                  {query.isFetchingNextPage ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Icon
                      icon="solar:alt-arrow-down-linear"
                      width={16}
                      height={16}
                    />
                  )}
                  {t("rewards.load_more")}
                </ButtonBase>
              </Box>
            )}
          </>
        )}
      </Box>
    </Fade>
  );
}
